import React, { useEffect, useState, useRef, useImperativeHandle } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { DatePicker, SelectField, Switch } from '../Base/Form'
import SelectLocation from './SelectLocation'
import { CircularProgress, Modal } from '@mui/material'
import { getData, postData } from '../../../js/helpers/apiHelper'
import Alert from '../Base/Alert'
import LoadingResultsModal from '../Modals/LoadingResultsModal'
import generateTimeOptions from '../../../js/helpers/generateTimeOptions'
import { useDispatch, useSelector } from 'react-redux'
import {
  setFilters,
  setStep,
  setInpathCars,
  setLatestSearch,
} from '../../../js/lib/redux/slices/booking'
import constants from '../../../js/lib/constants'

const now = new Date()
const oneYearLater = new Date()
const OneYearAndMonthLater = new Date()
oneYearLater.setFullYear(now.getFullYear() + 1)
OneYearAndMonthLater.setFullYear(now.getFullYear() + 1)
OneYearAndMonthLater.setMonth(OneYearAndMonthLater.getMonth() + 1)

const BookingWidget = React.forwardRef(
  ({ data, handleCancel, onFirstFetch }, ref) => {
    const { dictionary, logos, driverAgeOptions, config, currentValues } = data
    const timeOptions = generateTimeOptions()

    const diffLocationInitalCondition =
      currentValues?.pickUpLocation?.code !==
        currentValues?.dropOffLocation?.code ||
      currentValues?.pickUpLocation?.country !==
        currentValues?.dropOffLocation?.country ||
      currentValues?.pickUpLocation?.text !==
        currentValues?.dropOffLocation?.text
    const [dropOffAtDiffLocation, setDropOffAtDiffLocation] = useState(
      diffLocationInitalCondition
    )
    const [isSameDay, setIsSameDay] = useState(false)

    const [alertMessages, setAlertMessages] = useState([])
    const [loading, setLoading] = useState(false)
    const [isGREngine, setIsGREngine] = useState(
      window.inpathData?.isGREngine ?? false
    )

    const dispatch = useDispatch()

    const pickUpDateRef = useRef(null)
    const pickUpTimeRef = useRef(null)
    const dropOffDateRef = useRef(null)
    const dropOffTimeRef = useRef(null)

    const { validation } = dictionary
    const schema = z.object({
      pickUpLocation: z.object(
        {
          text: z.string(),
          value: z.string(),
          country: z.string(),
        },
        { invalid_type_error: validation?.required }
      ),
      dropOffLocation: z
        .object(
          {
            text: z.string(),
            value: z.string(),
            country: z.string(),
          },
          { invalid_type_error: validation?.required }
        )
        .optional(),
      pickUpDate: z.string(),
      pickUpTime: z.string(),
      dropOffDate: z.string(),
      dropOffTime: z.string(),
      // driverAge: z.string().optional(),
    })

    const {
      handleSubmit,
      control,
      setValue,
      watch,
      formState: { errors },
    } = useForm({
      resolver: zodResolver(schema),
      mode: 'onTouched',
      defaultValues: currentValues
        ? {
            ...currentValues,
            pickUpLocation: currentValues.pickUpLocation
              ? {
                  text: currentValues.pickUpLocation.text,
                  value: currentValues.pickUpLocation.code,
                  country: currentValues.pickUpLocation.country,
                }
              : '',
            dropOffLocation: currentValues.dropOffLocation
              ? {
                  text: currentValues.dropOffLocation.text,
                  value: currentValues.dropOffLocation.code,
                  country: currentValues.dropOffLocation.country,
                }
              : '',
          }
        : {},
    })

    const onSubmit = async (data) => {
      const body = {
        ...data,
        pickUpLocation: {
          code: data.pickUpLocation.value,
          country: data.pickUpLocation.country,
          text: data.pickUpLocation.text,
        },
        dropOffLocation: {
          code: dropOffAtDiffLocation
            ? data.dropOffLocation.value
            : data.pickUpLocation.value,
          country: dropOffAtDiffLocation
            ? data.dropOffLocation.country
            : data.pickUpLocation.country,
          text: dropOffAtDiffLocation
            ? data.dropOffLocation.text
            : data.pickUpLocation.text,
        },
        brands:
          window.inpathData?.records?.brands?.map((brand) => ({
            brandCode: brand.brandCode,
            brandSortOrder: brand.brandSortOrder,
          })) || [],
        promos: currentValues?.promos,
        isGREngine, // persisted from init response
      }

      setAlertMessages([])
      setLoading(true)

      // store latest search criteria so other components (e.g. BookingStep1) can use them
      dispatch(
        setLatestSearch({
          ...data,
          pickUpLocation: {
            code: data.pickUpLocation.value,
            country: data.pickUpLocation.country,
            text: data.pickUpLocation.text,
          },
          dropOffLocation: {
            code: dropOffAtDiffLocation
              ? data.dropOffLocation.value
              : data.pickUpLocation.value,
            country: dropOffAtDiffLocation
              ? data.dropOffLocation.country
              : data.pickUpLocation.country,
            text: dropOffAtDiffLocation
              ? data.dropOffLocation.text
              : data.pickUpLocation.text,
          },
        })
      )

      try {
        const response = await postData(config?.endpoints?.findCarInpath, body)
        if (response.hasError) {
          handleErrors(response.errorMessages)
        } else {
          dispatch(setInpathCars(response.data?.cars || []))
          dispatch(setStep(constants.booking.pageNumber.vehicles))
          dispatch(setFilters({}))
        }
      } catch (e) {
        handleErrors([dictionary.defaultErrorMessage])
      } finally {
        setLoading(false)
      }
    }

    const handleErrors = (messages) => {
      setAlertMessages(messages)
      document
        .querySelector('.s-booking-widget__form')
        .scrollTo({ behavior: 'smooth', top: 0 })
    }

    useEffect(() => {
      if (watch('pickUpDate') && watch('dropOffDate')) {
        const pickUp = new Date(watch('pickUpDate')).getTime()
        const dropOff = new Date(watch('dropOffDate')).getTime()
        if (pickUp > dropOff) {
          setValue('dropOffDate', '')
        } else if (pickUp === dropOff) {
          setIsSameDay(true)
        } else {
          setIsSameDay(false)
        }
      }
    }, [watch('pickUpDate'), watch('dropOffDate')])

    useEffect(() => {
      if (watch('pickUpDate') && !watch('pickUpTime')) {
        setValue('pickUpTime', '10:00', { shouldValidate: true })
      }
    }, [watch('pickUpDate'), watch('pickUpTime')])

    useEffect(() => {
      if (watch('dropOffDate')) {
        const currentDropOffTime = watch('dropOffTime')

        // Check if same day and pickup time is set
        if (isSameDay && watch('pickUpTime')) {
          const pickUpTimeIndex = timeOptions.indexOf(watch('pickUpTime'))
          const availableTimes = timeOptions.filter(
            (_, index) => index > pickUpTimeIndex
          )

          // Check if current dropoff time is invalid (not in available times or not set)
          const isInvalidTime =
            !currentDropOffTime || !availableTimes.includes(currentDropOffTime)

          if (isInvalidTime && availableTimes.length > 0) {
            // Use first available time after pickup
            setValue('dropOffTime', availableTimes[0], { shouldValidate: true })
          }
        } else if (!currentDropOffTime) {
          // Different days and no time set - use default
          setValue('dropOffTime', '10:00', { shouldValidate: true })
        }
      }
    }, [
      watch('dropOffDate'),
      watch('dropOffTime'),
      isSameDay,
      watch('pickUpTime'),
    ])

    useEffect(() => {
      const handleLoginSuccess = (event) => {
        setUserId(event.detail.isLoggedIn ? event.detail.userId : '')
      }
      window.addEventListener('loggedIn', handleLoginSuccess)

      return () => {
        window.removeEventListener('loggedIn', handleLoginSuccess)
      }
    }, [])

    const initFindCars = async () => {
      const currentValues =
        data.currentValues || window.inpathData?.currentValues

      if (!currentValues?.pickUpLocation) {
        return
      }

      const body = {
        ...currentValues,
        pickUpLocation: {
          code: currentValues.pickUpLocation.code,
          country: currentValues.pickUpLocation.country,
          text: currentValues.pickUpLocation.text,
        },
        dropOffLocation: {
          code: dropOffAtDiffLocation
            ? currentValues.dropOffLocation.code
            : currentValues.pickUpLocation.code,
          country: dropOffAtDiffLocation
            ? currentValues.dropOffLocation.country
            : currentValues.pickUpLocation.country,
          text: dropOffAtDiffLocation
            ? currentValues.dropOffLocation.text
            : currentValues.pickUpLocation.text,
        },
        brands:
          window.inpathData?.records?.brands?.map((brand) => ({
            brandCode: brand.brandCode,
            brandSortOrder: brand.brandSortOrder,
          })) || [],
        isGREngine,
      }

      setAlertMessages([])
      setLoading(true)

      try {
        const response = await postData(config?.endpoints?.findCarInpath, body)
        if (response.hasError) {
          handleErrors(response.errorMessages)
        } else {
          if (typeof response.data?.isGREngine !== 'undefined') {
            setIsGREngine(response.data.isGREngine)
          }
          dispatch(setInpathCars(response.data?.cars || []))
          dispatch(setStep(constants.booking.pageNumber.vehicles))
          dispatch(setFilters({}))
          if (onFirstFetch) onFirstFetch()
        }
      } catch (e) {
        handleErrors([dictionary.defaultErrorMessage])
      } finally {
        setLoading(false)
      }
    }

    useImperativeHandle(ref, () => ({
      initFindCars,
    }))

    const handlePickUpDateChange = (selectedDate) => {
      if (!selectedDate) {
        pickUpTimeRef.current?.open()
        return
      }

      const nextDay = new Date(selectedDate)
      nextDay.setDate(nextDay.getDate() + 1)
      const currentDropOffDate = watch('dropOffDate')

      // Only update drop-off date if it's not set or is earlier than next day
      const shouldUpdateDropOff =
        !currentDropOffDate ||
        new Date(currentDropOffDate).getTime() < nextDay.getTime()

      if (shouldUpdateDropOff) {
        setValue('dropOffDate', nextDay.toISOString().split('T')[0], {
          shouldValidate: true,
        })
      }

      pickUpTimeRef.current?.open()
    }

    return (
      <div className="s-booking-widget">
        <div className="page-container">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className={'s-booking-widget__form'}
            autocomplete="off">
            {alertMessages?.length > 0 && <Alert messages={alertMessages} />}
            <Switch
              name={'dropOffSwitch'}
              label={dictionary.locationSwitch}
              checked={dropOffAtDiffLocation}
              onChange={setDropOffAtDiffLocation}
            />

            <div className="s-booking-widget__inputs-grid ">
              <div className="s-booking-widget__location-fields">
                <SelectLocation
                  name={'pickUpLocation'}
                  label={
                    dropOffAtDiffLocation
                      ? dictionary.pickUpLabel
                      : dictionary.pickUpDropOffLabel
                  }
                  control={control}
                  setValue={setValue}
                  watch={watch}
                  error={errors.pickUpLocation}
                  locationSearchApi={config?.endpoints?.locationSearch}
                  locationFindApi={config?.endpoints?.locationFind}
                  dictionary={dictionary}
                />
                {dropOffAtDiffLocation && (
                  <SelectLocation
                    name={'dropOffLocation'}
                    label={dictionary.dropOffLabel}
                    control={control}
                    setValue={setValue}
                    watch={watch}
                    error={errors.dropOffLocation}
                    locationSearchApi={config?.endpoints?.locationSearch}
                    locationFindApi={config?.endpoints?.locationFind}
                    dictionary={dictionary}
                  />
                )}
              </div>

              <div className="s-booking-widget__date-time">
                <DatePicker
                  ref={pickUpDateRef}
                  name="pickUpDate"
                  label={dictionary.pickupDateLabel}
                  control={control}
                  setValue={setValue}
                  disableBefore={new Date()}
                  disableAfter={oneYearLater}
                  error={errors.pickUpDate}
                  dictionary={dictionary}
                  defaultValue={currentValues?.pickUpDate}
                  onChange={handlePickUpDateChange}
                />

                <SelectField
                  ref={pickUpTimeRef}
                  name={'pickUpTime'}
                  control={control}
                  label={dictionary.timeLabel}
                  options={timeOptions?.map((option) => ({
                    value: option,
                    text: option,
                  }))}
                  error={errors.pickUpTime}
                  isSelectTime
                  onChange={() => dropOffDateRef.current?.open()}
                />
              </div>

              <div className="s-booking-widget__date-time">
                <DatePicker
                  ref={dropOffDateRef}
                  name="dropOffDate"
                  label={dictionary.dropOffDateLabel}
                  control={control}
                  setValue={setValue}
                  disableBefore={
                    watch('pickUpDate')
                      ? new Date(watch('pickUpDate'))
                      : new Date()
                  }
                  disableAfter={OneYearAndMonthLater}
                  error={errors.dropOffDate}
                  dictionary={dictionary}
                  defaultValue={currentValues?.dropOffDate}
                  onChange={() => dropOffTimeRef.current?.open()}
                />
                <SelectField
                  ref={dropOffTimeRef}
                  name={'dropOffTime'}
                  control={control}
                  label={dictionary.timeLabel}
                  options={
                    watch('pickUpTime') && isSameDay
                      ? timeOptions
                          ?.filter(
                            (_, index) =>
                              index > timeOptions.indexOf(watch('pickUpTime'))
                          )
                          ?.map((option) => ({
                            value: option,
                            text: option,
                          }))
                      : timeOptions?.map((option) => ({
                          value: option,
                          text: option,
                        }))
                  }
                  error={errors.dropOffTime}
                />
              </div>
            </div>

            <div className="s-booking-widget__footer">
              <div className="s-booking-widget__logos">
                {logos?.map((logo) => (
                  <img key={logo.name} src={logo.url} alt={logo.alt} />
                ))}
              </div>
              <div className="s-booking-widget__actions">
                <button
                  type="button"
                  className="btn s-booking-widget__cancel"
                  onClick={handleCancel}
                  title={dictionary.cancelBtn}>
                  {dictionary.cancelBtn}
                </button>

                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={loading}
                  title={dictionary.findCarBtn}>
                  {dictionary.findCarBtn}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    )
  }
)

export default React.forwardRef((props, ref) => {
  const [data, setData] = useState()
  const { latestSearch } = useSelector((state) => state.booking)

  useEffect(() => {
    getData(window.bookingWidgetEndpoint).then((res) => {
      const apiData = res?.data || {}

      const domCurrentValues = window.inpathData?.currentValues
      const domLogos = window.inpathData?.logos

      const mergedData = {
        ...apiData,
        ...(domLogos && { logos: domLogos }),
      }

      const initialCurrentValues = latestSearch || domCurrentValues

      if (initialCurrentValues) {
        mergedData.currentValues = initialCurrentValues
      }

      setData(mergedData)
    })
  }, [])

  if (!data) {
    return (
      <div className="d-flex justify-content-center py-4">
        <CircularProgress />
      </div>
    )
  }

  return <BookingWidget {...props} data={data} ref={ref} />
})
